import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';

@Component({
  selector: 'bhub-text-cell',
  standalone: false,
  templateUrl: './text-cell.component.html',
  styleUrl: './text-cell.component.scss'
})
export class TextCellComponent extends BaseCellComponent implements OnChanges {
  @Input() viewManager: ViewManager | null = null;
  
  displayText: string = '';
  fontSize: number = 12;
  textAlign: string = 'center';
  fontWeight: string = 'normal';

  ngOnChanges(changes: SimpleChanges): void {
    // Check if viewManager changed
    if (changes['viewManager']) {
      // Update display when viewManager changes (which includes context data changes)
      this.updateDisplayProperties();
    }
  }

  protected initializeCell(): void {
    this.updateDisplayProperties();
    this.initializeContextVariable();
  }

  protected onCellOrDataChange(): void {
    this.updateDisplayProperties();
    this.initializeContextVariable();
  }

  protected onCellUpdate(): void {
    this.updateDisplayProperties();
    this.initializeContextVariable();
  }

  protected onDataSet(data: any): void {
    // Update display when external data changes
    this.updateDisplayProperties();
  }

  reloadData(): void {
    // Override to refresh display properties when context data changes
    this.updateDisplayProperties();
    this.initializeContextVariable();
  }

  private initializeContextVariable(): void {
    // Initialize context variable if configured for static text
    const textMode = this.getConfigValue('textMode', 'static') as 'static' | 'dynamic';
    const contextVariable = this.getConfigValue('contextVariable', '');
    
    if (textMode === 'static' && contextVariable && this.viewManager) {
      if (!this.viewManager.contextData) {
        this.viewManager.contextData = {};
      }
      
      // Store the text content in the context variable
      const textContent = this.getConfigValue('text', '');
      this.viewManager.contextData[contextVariable] = textContent;
    }
  }

  private updateDisplayProperties(): void {
    if (!this.cell) return;
    
    // Get styling properties
    this.fontSize = this.getConfigValue('fontSize', 12);
    this.textAlign = this.getConfigValue('textAlign', 'center');
    this.fontWeight = this.getConfigValue('fontWeight', 'normal');
    
    // Determine text content based on mode
    const textMode = this.getConfigValue('textMode', 'static') as 'static' | 'dynamic';
    
    if (textMode === 'dynamic') {
      this.displayText = this.getDynamicText();
    } else {
      this.displayText = this.getConfigValue('text', '');
    }
  }
  
  private getDynamicText(): string {
    const dataObjectKey = this.getDataBindingValue('dataObjectKey');
    const fieldName = this.getDataBindingValue('fieldName');

    if (!dataObjectKey || !fieldName || !this.viewManager?.contextData) {
      return '[No data configured]';
    }

    const dataObject = this.viewManager.contextData[dataObjectKey];
    if (!dataObject) {
      return `[${dataObjectKey} not found]`;
    }
    
    let value: any;
    
    if (Array.isArray(dataObject)) {
      // If it's an array, get the field from the first item
      if (dataObject.length > 0 && dataObject[0] && typeof dataObject[0] === 'object') {
        value = dataObject[0][fieldName];
      } else {
        return '[No data in array]';
      }
    } else if (typeof dataObject === 'object' && dataObject !== null) {
      // If it's a single object, get the field value
      value = dataObject[fieldName];
    } else {
      return '[Invalid data object]';
    }
    
    if (value === null || value === undefined) {
      return `[${fieldName} not found]`;
    }
    
    // Format the value appropriately
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    
    return String(value);
  }
}